import { GradientAvatar } from '@/shared/components';
import { Authorize } from '@/shared/components/authorize';
import { confirmMes, uploadAsync } from '@/shared/utilities';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useConversationSetting } from '../hooks/use-conversation-setting';
import { useConversationSettingForm } from '../hooks/use-conversation-setting-form';
import { Conversation } from '../models/conversation';
import { PERMISSION_KEYS } from '../models/permission';
import { useUpdateConversation } from '../queries/conversation.mutations';

export const ConversationSettingDialog: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen, conversation, closeDialog } = useConversationSetting();
  const { mutateAsync: updateConversation, isPending } = useUpdateConversation();

  const {
    handleSubmit,
    control,
    avatarFile,
    setAvatarFile,
    isUploadingAvatar,
    setIsUploadingAvatar,
    hasUnsavedChanges
  } = useConversationSettingForm(conversation);

  const onValid = useCallback(
    async (data: any) => {
      if (!conversation?.conversationId) return;

      try {
        setIsUploadingAvatar(true);

        const updateData = new Conversation(data);

        if (avatarFile) {
          updateData.conversationAvatarRef = await uploadAsync(avatarFile);
        }

        await updateConversation({
          conversationId: conversation.conversationId,
          data: updateData
        });
        closeDialog();
      } catch (error) {
        console.error('Failed to update conversation:', error);
        toast.error(t('conversations.toasts.updateError'));
      } finally {
        setIsUploadingAvatar(false);
      }
    },
    [conversation, avatarFile, setIsUploadingAvatar, updateConversation, closeDialog]
  );

  const attemptClose = useCallback(async () => {
    if (hasUnsavedChanges) {
      const shouldClose = await confirmMes({
        title: t('shared.dialog.confirmTitle'),
        description: t('conversations.settings.unsavedChangesDescription')
      });
      if (!shouldClose) return; // continue editing
    }
    closeDialog();
  }, [hasUnsavedChanges, closeDialog]);

  const handleClose = useCallback(() => {
    void attemptClose();
  }, [attemptClose]);

  return (
    <Authorize conversationId={conversation?.conversationId!} permissions={[PERMISSION_KEYS.CONVERSATION_UPDATE]}>
      <Dialog
        component='form'
        onSubmit={handleSubmit(onValid)}
        open={isOpen}
        fullWidth
        maxWidth='sm'
        onClose={handleClose}
      >
        <DialogTitle variant='h6'>{t('conversations.settings.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Stack gap={3} flexDirection='column' sx={{ pt: 2 }}>
            {/* Avatar Upload và Conversation Name */}
            <Stack direction='row' gap={2} alignItems='center'>
              <GradientAvatar
                enableUpload
                peerId={conversation?.conversationId}
                src={conversation?.conversationAvatarRef}
                name={conversation?.conversationName}
                variant={conversation?.conversationType === 'GROUP' ? 'rounded' : 'circular'}
                className='mex:!size-20'
                onChange={setAvatarFile}
              />

              {/* Conversation Name */}
              <Controller
                control={control}
                name='conversationName'
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    variant='filled'
                    size='small'
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={t('conversations.settings.namePlaceholder')}
                    className='mex:flex-1'
                    error={Boolean(error)}
                    helperText={error?.message}
                    sx={{
                      '& .MuiInputAdornment-root': {
                        margin: '0 !important'
                      },
                      '& .MuiInputBase-input.MuiFilledInput-input': {
                        padding: '12px 16px',
                        fontSize: '14px',
                        lineHeight: '1.5'
                      }
                    }}
                  />
                )}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions className='mex:px-6 mex:pb-4'>
          <Button onClick={handleClose} disabled={isPending || isUploadingAvatar}>
            {t('conversations.settings.cancelButton')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            className='mex:!min-w-24'
            type='submit'
            disabled={isPending || isUploadingAvatar || !hasUnsavedChanges}
            loading={isPending || isUploadingAvatar}
          >
            {isUploadingAvatar ? t('conversations.loading.uploadingImage') : t('shared.dialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Authorize>
  );
};
